import { GetStaticProps, GetStaticPropsContext } from 'next'

import PageProps from '@/interfaces/page'
import { tabsContentObj, pinnedStoryObj } from '@/interfaces/stories'
import { StoriesOverviewPage, Story } from '@/interfaces/schema'
import Main from '@/layouts/main'
import TabsSection from '@/sections/repeatable/tabs-section'
import {
  getRvdbNavigationData,
  getStoriesOverviewPageData,
} from '@/services/contentful/api'
import useWithinPageNav from '@/hooks/navigation/useWithinPageNav'
import StoriesListSection from '@/sections/repeatable/stories-list'
import useStoryCollection from '@/hooks/useStoryCollection'
import { ButtonPrimary } from '@/components/button'
import Snackbar from '@/components/snackbar'

const StoriesOverview = ({ data }: PageProps<StoriesOverviewPage>) => {
  const tabsContent = Object.values(tabsContentObj)
  const pinnedStoryValues = Object.values(pinnedStoryObj)

  const { activeElementIndex, handleChangeActiveElemIndex } = useWithinPageNav({
    numOfFormElements: tabsContent.length || 0,
  })

  const activePinnedStory = data[
    pinnedStoryValues[activeElementIndex] as keyof typeof data
  ] as Story

  const {
    searchQuery,
    isSearchActive,
    handleInputSearchQueryChange,
    handleSubmitSearch,
    storiesData,
    errorMessage,
    isErrorSnackbarOpen,
    handleCloseErrorSnackbar,
    handleLoadMore,
    handleChangeStoryType,
    totalCount,
  } = useStoryCollection({
    storyType: tabsContent[activeElementIndex],
  })

  const handleChangeStoryTab = (index: number) => {
    handleChangeActiveElemIndex(index)
    handleChangeStoryType()
  }

  return (
    <Main withGradient={false}>
      <TabsSection
        title={data.title}
        tabsContent={tabsContent}
        activeElementIndex={activeElementIndex}
        handleChangeActiveElemIndex={handleChangeStoryTab}
        handleInputSearchQueryChange={handleInputSearchQueryChange}
        searchQuery={searchQuery}
        handleSubmitSearch={handleSubmitSearch}
      />
      <StoriesListSection
        isSearchActive={isSearchActive}
        activePinnedStory={activePinnedStory}
        storiesData={storiesData}
      />
      {totalCount !== storiesData?.length && (
        <ButtonPrimary onClick={handleLoadMore}>
          Laad meer verhalen
        </ButtonPrimary>
      )}
      <Snackbar
        message={errorMessage}
        isOpen={isErrorSnackbarOpen}
        onClose={handleCloseErrorSnackbar}
      />
    </Main>
  )
}

export const getStaticProps: GetStaticProps = async ({
  preview,
}: GetStaticPropsContext) => {
  const isPreview: boolean = !!preview ?? false
  const isRay = false

  const { data: pageData, error: pageError } = await getStoriesOverviewPageData(
    isPreview,
    isRay
  )
  const { data: navData, error: navError } = await getRvdbNavigationData(
    isPreview
  )

  return {
    props: {
      data: { navData, pageData },
      error: { pageError, navError },
    },
    revalidate: 60,
  }
}

export default StoriesOverview
